import { Users } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useState } from 'react'
import { toApiError } from '@/api/errors'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { MenuLabel, Popover } from '@/components/ui/Popover'
import { Spinner } from '@/components/ui/Spinner'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'
import { presenceService, type OnlineUser, type OnlineUsers as OnlineList } from '@/services/core/presence.service'
import { useStores } from '@/stores'
import { PresenceDot, presencePageLine, presenceRoleLine } from './presence'
import { UserActivityDialog } from './UserActivityDialog'

const COUNT_POLL_MS = 60_000
const LIST_POLL_MS = 15_000

/**
 * «Кто онлайн» in the top bar, for users.activity.read: the number of people
 * in the admin console now (polled every minute), and on open the list of
 * them — active first, each with role, branch and the page they are on,
 * refreshed every 15 s. A row opens the person's activity.
 */
export const OnlineUsers = observer(function OnlineUsers() {
  const t = useT()
  const { session } = useStores()
  const enabled = session.can('users.activity.read')
  const [list, setList] = useState<OnlineList | null>(null)
  const [selected, setSelected] = useState<OnlineUser | null>(null)

  const refresh = useCallback(async () => {
    try {
      setList(await presenceService.online())
    } catch {
      // The badge keeps its last value; the open list shows real errors.
    }
  }, [])

  useEffect(() => {
    if (!enabled) return
    void refresh()
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refresh()
    }, COUNT_POLL_MS)
    return () => window.clearInterval(timer)
  }, [enabled, refresh])

  if (!enabled) return null
  const count = list?.items.length ?? 0

  return (
    <>
      <Popover
        align="end"
        kind="dialog"
        trigger={({ toggle, ...aria }) => (
          <Button
            variant="ghost"
            size="icon"
            {...aria}
            onClick={() => toggle()}
            aria-label={`${t('layout.presence.open')} · ${t('layout.presence.count', { count })}`}
            title={t('layout.presence.open')}
            className="relative"
          >
            <Users className="size-4" />
            {count > 0 && (
              <span className="num absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-success px-1 text-[10px] font-semibold leading-none text-white">
                {count > 99 ? '99+' : count}
              </span>
            )}
          </Button>
        )}
      >
        {(close) => (
          <OnlinePanel
            onList={setList}
            onOpen={(user) => {
              close()
              setSelected(user)
            }}
          />
        )}
      </Popover>
      {selected && <UserActivityDialog user={selected} onClose={() => setSelected(null)} />}
    </>
  )
})

/** The open dropdown: mounted only while open, so it polls only then. */
const OnlinePanel = observer(function OnlinePanel({
  onList,
  onOpen,
}: {
  onList: (list: OnlineList) => void
  onOpen: (user: OnlineUser) => void
}) {
  const t = useT()
  const { session, ui } = useStores()
  const [list, setList] = useState<OnlineList | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    const controller = new AbortController()
    const load = async () => {
      try {
        const next = await presenceService.online(controller.signal)
        setList(next)
        onList(next)
        setError('')
      } catch (cause) {
        if (!controller.signal.aborted) setError(toApiError(cause).message)
      }
    }
    void load()
    const timer = window.setInterval(() => void load(), LIST_POLL_MS)
    return () => {
      controller.abort()
      window.clearInterval(timer)
    }
  }, [onList])

  const me = session.profile?.id

  return (
    <div className="flex w-[min(380px,calc(100vw-32px))] flex-col">
      <div className="flex items-center justify-between gap-2 pr-2">
        <MenuLabel>{t('layout.presence.title')}</MenuLabel>
        {list && (
          <span className="num text-[length:calc(11.5px*var(--text-scale))] text-muted">
            {t('layout.presence.activeCount', { count: list.active })} · {t('layout.presence.idleCount', { count: list.idle })}
          </span>
        )}
      </div>
      <div className="max-h-[min(440px,60vh)] overflow-y-auto">
        {error && <p className="px-2.5 py-3 text-[length:calc(12.5px*var(--text-scale))] text-danger">{t('layout.presence.failed')}: {error}</p>}
        {!list && !error && (
          <div className="flex justify-center py-4">
            <Spinner />
          </div>
        )}
        {list && list.items.length === 0 && (
          <p className="px-2.5 py-6 text-center text-[length:calc(13px*var(--text-scale))] text-muted">{t('layout.presence.empty')}</p>
        )}
        {list?.items.map((user) => (
          <button
            key={user.profile_id}
            type="button"
            onClick={() => onOpen(user)}
            className="flex w-full items-start gap-2.5 rounded-[calc(8px*var(--radius-scale))] px-2.5 py-2 text-left transition-colors hover:bg-hover"
          >
            <span className="relative mt-0.5 shrink-0">
              <Avatar name={user.display_name} src={user.avatar_url} size={32} />
              <PresenceDot status={user.status} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex items-baseline gap-1.5">
                <span className="truncate text-[length:calc(13px*var(--text-scale))] font-medium text-fg">{user.display_name}</span>
                {user.profile_id === me && <span className="shrink-0 text-[length:calc(11px*var(--text-scale))] text-subtle">({t('layout.presence.you')})</span>}
              </span>
              {presenceRoleLine(user, ui.contentLocale) && (
                <span className="block truncate text-[length:calc(11.5px*var(--text-scale))] text-muted">{presenceRoleLine(user, ui.contentLocale)}</span>
              )}
              <span className={cn('block truncate text-[length:calc(11.5px*var(--text-scale))]', user.status === 'idle' ? 'text-warning' : 'text-fg-2')}>
                {presencePageLine(user, t)}
              </span>
            </span>
            {user.tabs > 1 && (
              <span className="num shrink-0 pt-0.5 text-[length:calc(11px*var(--text-scale))] text-subtle">{t('layout.presence.tabs', { count: user.tabs })}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  )
})
